/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@one-thd/sui-atomic-components';
import { Price } from '@thd-olt-component-react/price';

import UnavailableBundleMessage from './UnavailableBundleMessage.component';
import helpers from '../helpers';

/**
 * This component is responsible for rendering the price or an appropriate availability message
 * for a given bundle. Depending on the bundle's state, it displays one of the following:
 * - A notification link if the bundle is out of stock (OOS).
 * - A link to shop more bundles if the bundle is hidden or discontinued.
 * - The price of the bundle if it is available.
 */
export const BundlePrice = ({ bundle }) => {
  const { isOOS, isHiddenItem, isDiscontinued } = bundle?.bundleSpecificationDetails || {};
  const { shopMoreLinkText, unavailableBundleMessageText, unavailableShopMoreLink } = bundle?.features || {};

  const handleClick = useCallback(helpers.goToTop);

  if (isOOS) {
    return (
      <UnavailableBundleMessage unavailableBundleMessageText={unavailableBundleMessageText}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link component="button" onClick={handleClick} variant="body-base">
          Notify me when the bundle is available again
        </Link>
      </UnavailableBundleMessage>
    );
  }

  const { breadCrumbs } = bundle?.taxonomy || {};
  const url = unavailableShopMoreLink || breadCrumbs?.at(-1)?.url || '';

  if (isHiddenItem || isDiscontinued) {
    return (
      <UnavailableBundleMessage unavailableBundleMessageText={unavailableBundleMessageText}>
        <Link href={url} target="_blank" variant="body-base">
          {shopMoreLinkText}
        </Link>
      </UnavailableBundleMessage>
    );
  }

  return (
    <Price
      itemId={bundle.itemId}
      product={bundle}
      channel="desktop"
      type="simple"
      displayEachUom={false}
      clsRemediation={{
        placeholders: true,
        preservePlaceholders: true
      }}
    />
  );
};

BundlePrice.propTypes = {
  bundle: PropTypes.shape({
    itemId: PropTypes.string,
    bundleSpecificationDetails: PropTypes.shape({
      isOOS: PropTypes.bool,
      isHiddenItem: PropTypes.bool,
      isDiscontinued: PropTypes.bool,
    }),
    taxonomy: PropTypes.shape({
      breadCrumbs: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string,
      })),
    }),
    pricing: PropTypes.shape({}),
    features: PropTypes.shape({
      shopMoreLinkText: PropTypes.string,
      unavailableBundleMessageText: PropTypes.string
    })
  }).isRequired,
};